import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { ArrowDownToLine, ArrowUpFromLine, Users, Server, TrendingUp, RefreshCw, Wallet, CheckCircle2, Clock, XCircle } from "lucide-react";
import DetailStatCardSkeleton from "@/shared/components/skeletons/DetailStatCardSkeleton";
import { Button } from "@/shared/components/ui/button";
import StatCard from "@/shared/components/dashboard/StatCard";
import AreaStatsChart from "@/shared/components/dashboard/TransactionChart";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import * as transactionService from "@/shared/api/services/transactionService";
import * as merchantService from "@/shared/api/services/merchantService";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import ProviderStatus from "@/shared/components/dashboard/ProviderStatus";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange } from "@/shared/utils/dateFilters";
import utilities from "@/shared/utils/utilities";

export default function AdminDashboard() {

  const { t } = useLanguage();
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  // Get date range based on filter
  const dateRange = React.useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  const startISO = dateRange.startDate.toISOString();
  const endISO = dateRange.endDate.toISOString();

  const { data: dashboardStats, refetch: refetchDashboardStats, isPending: isLoadingStats } = useQuery({
    queryKey: ["dashboard-stats", dateRange],
    queryFn: () => {
      const startDate = startISO || undefined;
      const endDate = endISO || undefined;
      return adminService.getDashboardLiveMetrics(startDate, endDate);
    },
    placeholderData: (prev) => prev,
  });

  const { data: providers = [], refetch: refetchProviders, isLoading: isLoadingProviders } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
    select: (data) => [...(data || [])],
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: recentTransactionsResult, refetch: refetchRecentTransactions, isLoading: isLoadingRecent } = useQuery({
    queryKey: ["admin-recent-transactions", dateRange],
    queryFn: () => {
      const backendFilters = {};;
      return transactionService.getPaginatedTransactions(1, 5, "", backendFilters);
    },
  });

  const { data: chartDataResult, refetch: refetchChart, isLoading: isLoadingChart } = useQuery({
    queryKey: ["merchant-transactions-chart", startISO, endISO],
    queryFn: () => merchantService.getTransactionsChart(startISO, endISO, "PKR", null),
    placeholderData: (prev) => prev,
  });
  const chartData = chartDataResult || [];

  // stats cards mapping function
  const statsCards = [
    {
      title: t("balance"),
      value: utilities.formatCurrency(dashboardStats?.currentBalance),
      icon: Wallet,
      iconColor: "bg-purple-500",
      delay: 0,
    },
    {
      title: t("totalDeposits"),
      value: utilities.formatCurrency(dashboardStats?.allTimeDeposits),
      icon: ArrowDownToLine,
      iconColor: "bg-blue-500",
      delay: 0.05,
    },
    {
      title: t("totalWithdrawals"),
      value: utilities.formatCurrency(dashboardStats?.allTimeWithdrawals),
      icon: ArrowUpFromLine,
      iconColor: "bg-emerald-500",
      delay: 0.1,
    },
    {
      title: t("activeMerchants"),
      value: `${Number(dashboardStats?.activeMerchants ?? 0)}`,
      icon: Users,
      iconColor: "bg-amber-500",
      delay: 0.15,
    },
    {
      title: t("successful"),
      value: `${Number(dashboardStats?.successfulTxCount ?? 0)}`,
      icon: CheckCircle2,
      iconColor: "bg-emerald-500",
      delay: 0.2,
    },
    {
      title: t("pending"),
      value: `${Number(dashboardStats?.pendingTxCount ?? 0)}`,
      icon: Clock,
      iconColor: "bg-amber-500",
      delay: 0.25,
    },
    {
      title: t("declined"),
      value: `${Number(dashboardStats?.declinedTxCount ?? 0)}`,
      icon: XCircle,
      iconColor: "bg-red-500",
      delay: 0.3,
    },
  ];

  const transactions = mapGlobalTransactions(recentTransactionsResult?.data || []);

  const selectedMerchant = selectedTx
    ? (selectedTx.merchant?.id
      ? selectedTx.merchant
      : (selectedTx.merchant_id ? merchants.find((m) => m.id === selectedTx.merchant_id) : null))
    : null;

  const selectedReseller = selectedTx
    ? (selectedTx.reseller?.id
      ? selectedTx.reseller
      : (selectedTx.reseller_id
        ? (resellers.find((r) => r.id === selectedTx.reseller_id) || { id: selectedTx.reseller_id, name: selectedTx.reseller?.name || selectedTx.reseller_name })
        : (selectedMerchant?.createdByResellerId
          ? resellers.find((r) => r.id === selectedMerchant.createdByResellerId)
          : null)))
    : null;

  const selectedEngine = null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
          <p className="text-slate-500">{t("paymentOrchestration")}</p>
        </div>
        <div className="flex items-center gap-3">
          <DateFilter
            value={dateFilter}
            onChange={setDateFilter}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              setIsRefreshing(true);
              Promise.all([
                refetchDashboardStats(),
                refetchProviders(),
                refetchRecentTransactions(),
              ]).finally(() => setIsRefreshing(false));
            }}
            className="bg-white"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {isLoadingStats
          ? statsCards.map((_, i) => <DetailStatCardSkeleton key={i} />)
          : statsCards.map((card, index) => (
            <StatCard
              key={index}
              title={card.title}
              value={card.value}
              icon={card.icon}
              iconColor={card.iconColor}
              delay={card.delay}
            />
          ))}
      </div>

      {/* Charts & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AreaStatsChart data={chartData} currency="Rs" title="Transaction Volume" />
        </div>
        <ProviderStatus providers={providers} isLoading={isLoadingProviders} />
      </div>

      {/* Recent Transactions & Quick Actions */}
      <div className="grid grid-cols-1 gap-y-6">
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">{t("recentTransactions")}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(createPageUrl("Transactions"))}
            >
              {t("viewAll")}
            </Button>
          </div>
          <AdminTransactionTable
            transactions={transactions}
            isLoading={isLoadingRecent}
            onView={setSelectedTx}
            showMerchant={true}
            showReseller={false}
            showEngine={false}
            merchants={merchants}
            resellers={resellers}
            engines={engines}
            isQRCode={false}
            mixedTypes={true}
          />
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">{t("quickActions")}</h3>
          <div className="space-y-3">
            <Button asChild variant="outline" className="w-full justify-start gap-3 h-12">
              <Link to={createPageUrl("Merchants")}>
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{t("addMerchant")}</p>
                  <p className="text-xs text-slate-500">{t("onboardNewMerchant")}</p>
                </div>
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full justify-start gap-3 h-12">
              <Link to={createPageUrl("Providers")}>
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Server className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{t("configureProvider")}</p>
                  <p className="text-xs text-slate-500">{t("addPaymentProvider")}</p>
                </div>
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full justify-start gap-3 h-12">
              <Link to={createPageUrl("Routing")}>
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{t("routingRules")}</p>
                  <p className="text-xs text-slate-500">{t("configureRouting")}</p>
                </div>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>

      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        engine={selectedEngine}
        reseller={selectedReseller}
        merchant={selectedMerchant}
        isQRCode={selectedTx?.type !== "withdrawal"}
      />
    </div>
  );
}
